import { format, addDays, nextFriday, nextMonday, nextTuesday, nextWednesday, nextThursday, nextSaturday, nextSunday, isAfter, isSameDay } from 'date-fns';
import { parseResolvedAppointmentToETDate } from '@/utils/calendarLinks';

/**
 * Resolves relative dates like "Friday at 2 PM" to absolute dates like "Friday, Sept 13 at 2 PM ET"
 */
export function resolveRelativeDate(relativeText: string, baseDate: Date = new Date()): string {
  if (!relativeText) return relativeText;

  const text = relativeText.toLowerCase().trim();
  
  // Extract time if present (2 PM, 2:30, 14:00, etc.)
  const timeMatch = text.match(/(\d{1,2}):?(\d{2})?\s*(am|pm|a\.m\.|p\.m\.)?/i);
  let hours = 9; // Default to 9 AM if no time specified
  let minutes = 0;
  
  if (timeMatch) {
    hours = parseInt(timeMatch[1]);
    minutes = timeMatch[2] ? parseInt(timeMatch[2]) : 0;
    
    // Enhanced AM/PM logic - default 1-7 to PM when no AM/PM specified
    if (timeMatch[3]) {
      // AM/PM explicitly specified
      const isPM = timeMatch[3].toLowerCase().includes('p');
      if (isPM && hours < 12) {
        hours += 12;
      } else if (!isPM && hours === 12) {
        hours = 0;
      }
    } else {
      // No AM/PM - smart defaults: 1-7 = PM, 8-12 = AM
      if (hours >= 1 && hours <= 7) {
        hours += 12; // Convert to PM
      } else if (hours === 12) {
        hours = 12; // Keep 12 as noon
      }
      // 8-11 stay as AM
    }
    
    console.debug(`Time parsing: "${timeMatch[0]}" -> ${hours}:${minutes.toString().padStart(2, '0')}`);
  }

  // Day resolution logic
  let targetDate: Date;
  
  if (text.includes('today')) {
    targetDate = new Date(baseDate);
  } else if (text.includes('tomorrow')) {
    targetDate = addDays(baseDate, 1);
  } else if (text.includes('monday') || text.includes('mon')) {
    targetDate = nextMonday(baseDate);
    if (isSameDay(targetDate, baseDate) && isAfter(baseDate, new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate(), hours, minutes))) {
      targetDate = addDays(targetDate, 7);
    }
  } else if (text.includes('tuesday') || text.includes('tue')) {
    targetDate = nextTuesday(baseDate);
    if (isSameDay(targetDate, baseDate) && isAfter(baseDate, new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate(), hours, minutes))) {
      targetDate = addDays(targetDate, 7);
    }
  } else if (text.includes('wednesday') || text.includes('wed')) {
    targetDate = nextWednesday(baseDate);
    if (isSameDay(targetDate, baseDate) && isAfter(baseDate, new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate(), hours, minutes))) {
      targetDate = addDays(targetDate, 7);
    }
  } else if (text.includes('thursday') || text.includes('thu')) {
    targetDate = nextThursday(baseDate);
    if (isSameDay(targetDate, baseDate) && isAfter(baseDate, new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate(), hours, minutes))) {
      targetDate = addDays(targetDate, 7);
    }
  } else if (text.includes('friday') || text.includes('fri')) {
    targetDate = nextFriday(baseDate);
    if (isSameDay(targetDate, baseDate) && isAfter(baseDate, new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate(), hours, minutes))) {
      targetDate = addDays(targetDate, 7);
    }
  } else if (text.includes('saturday') || text.includes('sat')) {
    targetDate = nextSaturday(baseDate);
    if (isSameDay(targetDate, baseDate) && isAfter(baseDate, new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate(), hours, minutes))) {
      targetDate = addDays(targetDate, 7);
    }
  } else if (text.includes('sunday') || text.includes('sun')) {
    targetDate = nextSunday(baseDate);
    if (isSameDay(targetDate, baseDate) && isAfter(baseDate, new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate(), hours, minutes))) {
      targetDate = addDays(targetDate, 7);
    }
  } else {
    // No day specified, return original text
    return relativeText;
  }

  // Set the resolved time
  targetDate.setHours(hours, minutes, 0, 0);
  
  // Format as "Friday, Sept 13 at 2:00 PM ET"
  const dayName = format(targetDate, 'EEEE');
  const dateStr = format(targetDate, 'MMM d');
  const timeStr = format(targetDate, 'h:mm a');
  
  return `${dayName}, ${dateStr} at ${timeStr} ET`;
}

/**
 * Clean and resolve appointment details from action data
 */
export function resolveAppointmentDetails(action: any): {
  date: string | null;
  time: string | null;
  service: string | null;
  location: string | null;
  notes: string | null;
  startDate: Date | null;
  endDate: Date | null;
} | null {
  // First try extractedData (preferred method)
  if (action?.action_data?.extractedData) {
    return resolveFromExtractedData(action);
  }
  
  // Fallback: parse appointment details from phrase
  if (action?.type === 'appointment_booked' && action.phrase) {
    return resolveFromPhrase(action);
  }
  
  return null;
}

function resolveFromExtractedData(action: any) {
  
  const { extractedData } = action.action_data;
  const baseDate = action.created_at ? new Date(action.created_at) : new Date();
  
  // Resolve relative dates to absolute dates
  let resolvedDate = extractedData.date;
  if (resolvedDate) {
    resolvedDate = resolveRelativeDate(resolvedDate, baseDate);
  }
  
  let resolvedTime = extractedData.time;
  if (resolvedTime && !resolvedDate) {
    // If only time is specified, assume today/tomorrow
    resolvedTime = resolveRelativeDate(`today at ${resolvedTime}`, baseDate);
  }
  
  // Parse resolved appointment text to Date objects
  const appointmentText = resolvedDate || resolvedTime;
  let startDate: Date | null = null;
  let endDate: Date | null = null;
  
  if (appointmentText) {
    const parsedDates = parseResolvedAppointmentToETDate(appointmentText, baseDate);
    if (parsedDates) {
      startDate = parsedDates.startDate;
      endDate = parsedDates.endDate;
    }
  }
  
  return {
    date: resolvedDate || null,
    time: resolvedTime || null,
    service: extractedData.service || null,
    location: extractedData.location || null,
    notes: extractedData.notes || null,
    startDate,
    endDate
  };
}

function resolveFromPhrase(action: any) {
  const phrase = action.phrase || '';
  const baseDate = action.created_at ? new Date(action.created_at) : new Date();
  
  // Extract appointment details from phrase using patterns (enhanced to handle missing AM/PM)
  const normalizedPhrase = phrase.toLowerCase().trim();
  console.debug(`Parsing appointment phrase: "${normalizedPhrase}"`);
  
  const timePatterns = [
    // "Friday at 2 PM", "Monday at 10:30 AM" - with AM/PM
    /(?:on\s+)?(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\s+at\s+(\d{1,2}):?(\d{2})?\s*(am|pm|a\.m\.|p\.m\.)/i,
    // "tomorrow at 2 PM", "today at 3 PM" - with AM/PM
    /(today|tomorrow)\s+at\s+(\d{1,2}):?(\d{2})?\s*(am|pm|a\.m\.|p\.m\.)/i,
    // "2 PM Friday", "10:30 AM Monday" - with AM/PM
    /(\d{1,2}):?(\d{2})?\s*(am|pm|a\.m\.|p\.m\.)\s+(monday|tuesday|wednesday|thursday|friday|saturday|sunday)/i,
    // NEW: "Friday at 2", "Monday at 10:30" - without AM/PM
    /(?:on\s+)?(monday|tuesday|wednesday|thursday|friday|saturday|sunday)\s+at\s+(\d{1,2}):?(\d{2})?(?!\s*(?:am|pm))/i,
    // NEW: "tomorrow at 2", "today at 3" - without AM/PM  
    /(today|tomorrow)\s+at\s+(\d{1,2}):?(\d{2})?(?!\s*(?:am|pm))/i
  ];
  
  let extractedDate: string | null = null;
  let extractedTime: string | null = null;
  
  for (const pattern of timePatterns) {
    const match = normalizedPhrase.match(pattern);
    if (match) {
      console.debug(`Pattern matched:`, match);
      
      // Pattern 1, 2, 4, 5: day at time format
      if (match[1] && match[2]) {
        const day = match[1].toLowerCase();
        const hour = match[2];
        const minute = match[3] || '00';
        const period = match[4]; // May be undefined for patterns without AM/PM
        
        extractedDate = day;
        if (period) {
          extractedTime = `${hour}:${minute} ${period}`;
        } else {
          // No AM/PM - apply smart defaults and add PM for 1-7
          const hourNum = parseInt(hour);
          const smartPeriod = (hourNum >= 1 && hourNum <= 7) ? 'PM' : 'AM';
          extractedTime = `${hour}:${minute} ${smartPeriod}`;
        }
      }
      // Pattern 3: time day format (with AM/PM)
      else if (match[4] && match[1]) {
        const hour = match[1];
        const minute = match[2] || '00';
        const period = match[3];
        const day = match[4].toLowerCase();
        extractedDate = day;
        extractedTime = `${hour}:${minute} ${period}`;
      }
      
      console.debug(`Extracted - Date: "${extractedDate}", Time: "${extractedTime}"`);
      break;
    }
  }
  
  // If no structured time found, look for just day mentions
  if (!extractedDate) {
    const dayPattern = /(monday|tuesday|wednesday|thursday|friday|saturday|sunday|today|tomorrow)/i;
    const dayMatch = phrase.match(dayPattern);
    if (dayMatch) {
      extractedDate = dayMatch[1].toLowerCase();
    }
  }
  
  // If no structured time found, look for just time mentions (enhanced for missing AM/PM)
  if (!extractedTime) {
    const timePatterns = [
      // Time with AM/PM
      /(\d{1,2}):?(\d{2})?\s*(am|pm|a\.m\.|p\.m\.)/i,
      // Time without AM/PM
      /\b(\d{1,2}):?(\d{2})?(?!\s*(?:am|pm))(?=\s|$)/i
    ];
    
    for (const timePattern of timePatterns) {
      const timeMatch = normalizedPhrase.match(timePattern);
      if (timeMatch) {
        const hour = timeMatch[1];
        const minute = timeMatch[2] || '00';
        const period = timeMatch[3];
        
        if (period) {
          extractedTime = `${hour}:${minute} ${period}`;
        } else {
          // No AM/PM - apply smart defaults
          const hourNum = parseInt(hour);
          const smartPeriod = (hourNum >= 1 && hourNum <= 7) ? 'PM' : 'AM';
          extractedTime = `${hour}:${minute} ${smartPeriod}`;
        }
        
        console.debug(`Extracted standalone time: "${extractedTime}"`);
        break;
      }
    }
  }
  
  // Resolve dates using existing logic
  let resolvedDate = extractedDate ? resolveRelativeDate(extractedDate, baseDate) : null;
  let resolvedTime = extractedTime;
  
  if (resolvedTime && !resolvedDate) {
    resolvedTime = resolveRelativeDate(`today at ${resolvedTime}`, baseDate);
  } else if (extractedDate && extractedTime) {
    resolvedDate = resolveRelativeDate(`${extractedDate} at ${extractedTime}`, baseDate);
  }
  
  // Parse resolved appointment text to Date objects
  const appointmentText = resolvedDate || resolvedTime;
  let startDate: Date | null = null;
  let endDate: Date | null = null;
  
  if (appointmentText) {
    const parsedDates = parseResolvedAppointmentToETDate(appointmentText, baseDate);
    if (parsedDates) {
      startDate = parsedDates.startDate;
      endDate = parsedDates.endDate;
    }
  }
  
  return {
    date: resolvedDate || null,
    time: resolvedTime || null,
    service: null, // Can't extract service from phrase reliably
    location: null, // Can't extract location from phrase reliably  
    notes: null, // Never expose system prompts or raw phrase data
    startDate,
    endDate
  };
}